import { useSelector } from "react-redux";
import { Flex } from "antd";
import { SelectedConversaton } from "../../../store/slices/conversation.slice";
import { RootState } from "../../../store/store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BaseMessage } from "../../../interfaces/messages.type";
import useGetMessages from "../../../hooks/apis/messages.api";
import ChatCard from "./chat.card";
import { DateSeparator } from "../../generic/separators";
import { formatChatDate } from "../../../utils/dateFormat";
import UseScrollObserver from "../../../hooks/scroll-observer";
import { Conversation } from "../../../interfaces/conversations.type";
import {
    DisplayMessage,
    groupMessagesForDisplay,
} from "../../../store/slices/messages.slice";
import { createContactsByPhone } from "../../../utils/conversationDisplay";
import Loader from "../../generic/Loader";

const ChatList = ({
    ffmpegRef,
    conversation,
    deleteMessage,
    sendMessage
}: {
    conversation: SelectedConversaton;
    ffmpegRef: any;
    deleteMessage: Function;
    sendMessage: Function;
}) => {

    const { data } = useSelector((state: RootState) => state.messages);
    const { selectedAccount } = useSelector((state: RootState) => state.admin);
    const { data: conversations } = useSelector((state: RootState) => state.conversation);
    const { messages: offlineMessages } = useSelector((state: RootState) => state.offlineMessages);
    const { data: contacts } = useSelector((state: RootState) => state.contacts);
    const [offset, setOffset] = useState<number>(conversation.position ? Number(Math.floor(conversation.position / 20) * 20) : 0);
    const messagesRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const messageRefs = useRef<(HTMLDivElement | null)[]>([]); // To store references to message elements
    const lastScrollTopRef = useRef(0);
    const scrollDirectionRef = useRef<"up" | "down" | null>(null);
    const [searchInput, setSearchInput] = useState<string | null>(conversation.search ? conversation.search : null);
    const [scrollDirection, setScrollDirection] = useState<"up" | "down" | null>(null);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [visibleMessage, setVisibleMessage] = useState<number | null>(null);
    const [previousMessagesToFetch, setPreviousMessagesToFetch] = useState<number>(
        conversation.position ? Number(Math.floor(conversation.position / 20) * 20) : 0
    );
    const { mutate, isLoading } = useGetMessages();

    const conversationRef = useRef<Conversation | null>();

    // const markConversationRead = (conversation: UpdateConversation) => {
    //     updateConversation(conversation);
    // };

    const messages: BaseMessage[] = useMemo(() => {
        const messageData = data.find(
            message => message.conversation_id === conversation.conversation_id
        );
        const selectedParticipants = [...conversation.participants].sort().join("|");
        const pendingMessages = offlineMessages.filter(
            message =>
                message.conversation_id === conversation.conversation_id ||
                (
                    !message.conversation_id &&
                    message.did === conversation.did &&
                    [...(message.conversation_participants || [message.participant])]
                        .sort()
                        .join("|") === selectedParticipants
                )
        );
        const messageConversation = conversations.find(
            conv => conv.id === conversation.conversation_id
        );
        conversationRef.current = messageConversation || null;

        return [...(messageData?.messages || []), ...pendingMessages];
    }, [conversation, data, offlineMessages]);

    const displayMessages: DisplayMessage[] = useMemo(
        () => groupMessagesForDisplay(messages),
        [messages]
    );
    const sortedDisplayMessages = useMemo(
        () => displayMessages
            .slice()
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
        [displayMessages]
    );
    const contactsByPhone = useMemo(() => createContactsByPhone(contacts), [contacts]);

    // useEffect(() => {
    //     if (!conversationRef.current) {
    //         return;
    //     }

    //     const messageConversation = conversationRef.current;

    //     if (!isAdminView && messageConversation && (messageConversation.total_unread > 0 || messageConversation.unread)) {
    //         markConversationRead({
    //                 did: conversation.did,
    //                 participant: conversation.participant,
    //                 last_read_timestamp: new Date().toISOString(),
    //                 total_read: messageConversation.total_unread,
    //                 ...(messageConversation.unread ? { unread: false } : {})
    //               });
    //     }
    // }, []);

    UseScrollObserver({
        cardRefs: messageRefs,
        cards: displayMessages,
        setVisibleMessage
    });

    const fetchMessages = (offset: number, callback?: Function) => {
        if (!isLoading) {
            mutate(
                {
                    did: conversation.did,
                    conversation_id: conversation.conversation_id,
                    offset: offset,
                    limit: 20,
                    account: selectedAccount
                },
                {
                    onSuccess: data => {
                        if (callback) {
                            callback(data);
                        }
                    }
                }
            );
        }
    };

    useEffect(() => {
        if (messagesRef) {
            messagesRef.current = messages;
        }
    }, [messages]);

    useEffect(() => {
        if (searchInput) {
            const foundIndex = displayMessages.findIndex(
                message =>
                    message.send_id === conversation.searched_message_id ||
                    message.id === conversation.searched_message_id
            );

            if (foundIndex !== -1 && messageRefs.current[foundIndex]) {
                messageRefs.current[foundIndex]?.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
                setSearchInput(null);
            }
            if (displayMessages.length < 20) {
                fetchMessages(offset - 20);
            }
        }
    }, [displayMessages, searchInput]);

    useEffect(() => {
        fetchMessages(offset);
    }, []);

    useEffect(() => {
        if (displayMessages.length > 0 && scrollDirection === "down") {
            if (previousMessagesToFetch > 0 && !isLoading && visibleMessage !== null && visibleMessage < 10) {
                fetchMessages(previousMessagesToFetch - 20, () => {
                    setPreviousMessagesToFetch(previousMessagesToFetch - 20);
                });
            }
        } else if (displayMessages.length > 0 && scrollDirection === "up") {
            if (visibleMessage !== null && visibleMessage > displayMessages.length - 5 && !isLoading && hasMore) {
                fetchMessages(offset + 20, (data: { messages: BaseMessage[] }) => {
                    setOffset(offset + 20);
                    if (data.messages.length === 0) {
                        setHasMore(false);
                    }
                });
            }
        }
    }, [scrollDirection, visibleMessage]);

    const handleScroll = useCallback(() => {
        if (containerRef.current) {
            const currentScrollTop = containerRef.current.scrollTop;
            const nextScrollDirection =
                currentScrollTop > lastScrollTopRef.current
                    ? "down"
                    : currentScrollTop < lastScrollTopRef.current
                        ? "up"
                        : null;

            if (nextScrollDirection && nextScrollDirection !== scrollDirectionRef.current) {
                scrollDirectionRef.current = nextScrollDirection;
                setScrollDirection(nextScrollDirection);
            }

            lastScrollTopRef.current = currentScrollTop;
        }
    }, []);

    if (isLoading && sortedDisplayMessages.length === 0) {
        return (
            <Flex
                align="center"
                justify="center"
                style={{ height: "calc(100% - 220px)" }}
            >
                <Loader />
            </Flex>
        );
    }

    return (
        <div
            ref={containerRef}
            style={{
                height: "calc(100% - 220px)",
                overflow: "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column-reverse",
                padding: "5px",
                overflowAnchor: "none"
            }}
            onScroll={handleScroll}
        >
            {sortedDisplayMessages
                .map((message, index, arr) => {
                    const currentMessageDate = formatChatDate(message.timestamp);
                    const previousMessageDate = index < arr.length - 1 ? formatChatDate(arr[index + 1].timestamp) : null;
                    return (
                        <div
                            style={{
                                border: "none",
                                paddingBottom: "10px",
                                justifyContent: message.direction === "outbound" ? "flex-start" : "flex-end"
                            }}
                            key={message.send_id || message.clientId || message.id || index}
                        >
                            {" "}
                            {currentMessageDate !== previousMessageDate && <DateSeparator date={formatChatDate(message.timestamp)} />}
                            {/* {lastIndex === index && <NewMessageSeparator />} */}
                            <div
                                data-index={index} // Add a data attribute to identify the index
                                id={`message-${index}`}
                                ref={el => (messageRefs.current[index] = el)}
                            >
                                <ChatCard
                                    sendMessage={sendMessage}
                                    deleteMessage={deleteMessage}
                                    key={message.send_id || message.clientId || message.id || index}
                                    ffmpegRef={ffmpegRef}
                                    contactsByPhone={contactsByPhone}
                                    message={message}
                                />
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

export default ChatList;
